import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { moduleDescription, moduleDisplayName } from '../../utils/moduleNames';
import axios from 'axios';
import { FiCheck, FiUsers, FiPackage } from 'react-icons/fi';
import { formatCurrency, formatDate } from '../../utils/format';
import { preferredCurrency } from '../../config/languages';

const API = process.env.REACT_APP_BACKEND_URL || '';

// narrowSymbol keeps the short sign (₺, $, ¥) in every locale; en-US would
// otherwise print "TRY 100".
const WHOLE_UNITS = { currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 0 };

const CURRENCY_SYMBOL = {
  TRY: '₺', USD: '$', EUR: '€', GBP: '£', JPY: '¥', RUB: '₽',
};

// Module marketing copy by key lives in landing.pricingSection.moduleCopy.*
// (backend `description` is English-only, modules_collection seed). Falls
// back to the backend description when the key is unknown.

const PricingSection = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [pricing, setPricing] = useState(null);
  const [fx, setFx] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [currency, setCurrency] = useState('TRY');

  useEffect(() => {
    axios.get(`${API}/api/billing/plan-pricing`).then(({ data }) => setPricing(data)).catch(() => {});
    axios.get(`${API}/api/billing/exchange-rates`).then(({ data }) => setFx(data)).catch(() => {});
  }, []);

  // Default the currency from the active language, but only pick one the
  // backend gateway actually supports. Otherwise the buyer would see USD
  // pricing on landing and then get force-flipped to TRY at /checkout —
  // surprise FX is bad UX. supported_currencies comes from /plan-pricing
  // which already encodes the active gateway's constraint (#390).
  useEffect(() => {
    if (!pricing) return;
    const wanted = preferredCurrency(i18n.language);
    const supported = pricing.supported_currencies || ['TRY'];
    setCurrency(supported.includes(wanted) ? wanted : 'TRY');
  }, [i18n.language, pricing]);

  // Convert a TRY price to the active display currency. Falls back to TRY
  // if FX hasn't loaded or the rate is missing.
  const convert = useMemo(() => (tryAmount) => {
    if (currency === 'TRY' || !fx?.rates) return Number(tryAmount || 0);
    const rate = fx.rates[currency];
    if (!rate || rate <= 0) return Number(tryAmount || 0);
    return Number(tryAmount || 0) / rate;
  }, [currency, fx]);

  const fmt = (amount) => {
    const fractionDigits = currency === 'JPY' ? 0 : (currency === 'TRY' ? 0 : 2);
    return formatCurrency(Number(amount || 0), currency, {
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

  if (!pricing) {
    return (
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 text-center text-gray-500">{t('common.loading')}</div>
      </section>
    );
  }

  // Always derive yearly from monthly × 12 × (1 − discount%). DB's
  // stored price_yearly may be stale (operators edited prices before
  // #455 made yearly auto-derived). Source of truth: monthly + pct.
  const discountPct = Number(pricing.yearly_discount_pct || 0);
  const computeYearly = (monthly) => Math.round(Number(monthly || 0) * 12 * (1 - discountPct / 100));
  const userPriceTRY = cycle === 'yearly'
    ? computeYearly(pricing.per_user_price_monthly)
    : pricing.per_user_price_monthly;
  const userPriceDisplay = convert(userPriceTRY);
  const cycleLabel = cycle === 'yearly' ? t('pricing.cycle_year') : t('pricing.cycle_month');

  // Show only the gateway-supported subset of TCMB currencies. Falls
  // back to ['TRY'] when /plan-pricing didn't surface a list (older
  // backend or missing field).
  const availableCurrencies = pricing.supported_currencies || ['TRY'];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-10">
          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
            {t('pricing.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">
            {t('pricing.heading')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('pricing.subheading', { trialDays: pricing.trial_days })}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <div className="inline-flex bg-white rounded-full p-1 shadow">
            {[
              ['monthly', t('pricing.monthly')],
              ['yearly', t('pricing.yearly_with_discount', { pct: pricing.yearly_discount_pct })],
            ].map(([v, label]) => (
              <button key={v} onClick={() => setCycle(v)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${cycle === v ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                {label}
              </button>
            ))}
          </div>
          {availableCurrencies.length > 1 && (
            <div className="inline-flex bg-white rounded-full p-1 shadow">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                      className="px-3 py-1.5 rounded-full text-sm font-semibold bg-transparent focus:outline-none">
                {availableCurrencies.map((c) => (
                  <option key={c} value={c}>{CURRENCY_SYMBOL[c] || ''} {c}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-600 relative">
            <span className="absolute -top-3 left-8 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
              {t('pricing.per_user_label')}
            </span>
            <FiUsers className="w-10 h-10 text-blue-600 mb-3" />
            <div className="text-4xl font-bold text-gray-900">
              {fmt(userPriceDisplay)}
              <span className="text-base font-normal text-gray-500">/{t('pricing.per_user_unit')}/{cycleLabel}</span>
            </div>
            {/* TRY equivalent for foreign-currency display — sets buyer
                expectation about the actual settlement amount before
                they reach checkout. */}
            {currency !== 'TRY' && (
              <div className="text-xs text-gray-400 mt-1">
                ≈ {formatCurrency(Number(userPriceTRY), 'TRY', WHOLE_UNITS)} TRY
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2 mb-5">
              {t('pricing.per_user_note')}
            </p>
            <ul className="text-sm text-gray-700 space-y-2">
              {['core', 'unlimited', 'pdf', 'portal', 'gateways', 'uptime'].map((f) => (
                // "gateways" lists provider brand names (PayTR / Iyzico / Paraşüt).
                <li key={f} className="flex items-start gap-2" {...(f === 'gateways' ? { 'data-i18n-ignore': '' } : {})}>
                  <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> {t(`pricing.feature_${f}`)}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <FiPackage className="w-10 h-10 text-purple-600 mb-3" />
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {t('pricing.modules_heading')}
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {t('pricing.modules_note')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {(pricing.available_modules || []).slice(0, 12).map((m) => {
                const tryPrice = cycle === 'yearly'
                  ? computeYearly(m.price_monthly)
                  : m.price_monthly;
                return (
                  <div key={m.key} className="border border-gray-100 rounded-lg p-2.5 hover:border-purple-300 transition">
                    <div className="flex justify-between items-baseline">
                      <span className="font-medium text-gray-800 flex items-center gap-1">
                        {m.icon && <span aria-hidden>{m.icon}</span>}
                        {moduleDisplayName(m, t)}
                      </span>
                      <span className="text-gray-600 text-sm whitespace-nowrap">
                        {fmt(convert(tryPrice))}
                        {currency !== 'TRY' && (
                          <span className="text-xs text-gray-400 ms-1">
                            ({formatCurrency(Number(tryPrice), 'TRY', WHOLE_UNITS)})
                          </span>
                        )}
                      </span>
                    </div>
                    {(() => {
                      const copyKey = `landing.pricingSection.moduleCopy.${m.key}`;
                      const desc = i18n.exists(copyKey) ? t(copyKey) : moduleDescription(m, t);
                      return desc ? (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{desc}</p>
                      ) : null;
                    })()}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 text-center">
              <button onClick={() => navigate('/modules-info')}
                      className="text-sm text-purple-600 hover:text-purple-800 font-semibold">
                {t('landing.pricingSection.seeModuleDetails')}
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-10">
          <button onClick={() => navigate(`/signup?cycle=${cycle}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-lg transition">
            {t('pricing.cta_start_trial', { trialDays: pricing.trial_days })}
          </button>
          <p className="text-xs text-gray-500 mt-3">
            {t('pricing.cta_footer')}
            {fx?.source === 'tcmb' && currency !== 'TRY' && (
              <span> · {t('pricing.tcmb_note', { date: formatDate(fx.fetched_at) })}</span>
            )}
          </p>
        </div>

        <PricingFAQ pricing={pricing} />
      </div>
    </section>
  );
};

// FAQ copy adapts to the active gateway: when PayTR is active the
// settlement-currency answer is TRY-only; when Iyzico is active it
// lists the gateway-supported set. Keeps buyer expectation aligned
// with what /checkout will actually do (#390).
const PricingFAQ = ({ pricing }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  const gateway = (pricing?.active_gateway || 'paytr').toLowerCase();
  const supported = pricing?.supported_currencies || ['TRY'];
  const trialDays = pricing?.trial_days || 14;

  const items = [
    {
      q: t('landing.pricingSection.faq.paymentsQuestion'),
      a: gateway === 'iyzico'
        ? t('landing.pricingSection.faq.paymentsAnswerIyzico')
        : t('landing.pricingSection.faq.paymentsAnswerPaytr'),
    },
    {
      q: t('landing.pricingSection.faq.currencyQuestion'),
      a: supported.length === 1
        ? t('landing.pricingSection.faq.currencyAnswerSingle')
        : t('landing.pricingSection.faq.currencyAnswerMulti', { currencies: supported.join(', ') }),
    },
    {
      q: t('landing.pricingSection.faq.trialQuestion', { days: trialDays }),
      a: t('landing.pricingSection.faq.trialAnswer', { days: trialDays }),
    },
    {
      q: t('landing.pricingSection.faq.autoRenewQuestion'),
      a: t('landing.pricingSection.faq.autoRenewAnswer'),
    },
    {
      q: t('landing.pricingSection.faq.refundQuestion'),
      a: t('landing.pricingSection.faq.refundAnswer'),
    },
    {
      q: t('landing.pricingSection.faq.seatsQuestion'),
      a: t('landing.pricingSection.faq.seatsAnswer'),
    },
  ];

  return (
    <div className="mt-16 max-w-3xl mx-auto">
      <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">{t('landing.pricingSection.faq.heading')}</h3>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-gray-50 transition"
              aria-expanded={open === i}
            >
              <span className="font-semibold text-gray-900">{item.q}</span>
              <span className={`text-blue-600 transition-transform ${open === i ? 'rotate-45' : ''}`}>+</span>
            </button>
            {open === i && (
              <div className="px-5 pb-4 text-sm text-gray-600">{item.a}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingSection;
